import React from 'react';
import { useNavigate } from 'react-router-dom';
import SPAButton from '../shared/SPAButton';
import TakeColumnButton from './TakeColumnButton';
import { needsAction, TEACHER_FIXES } from './repairs';
import type { AttachColumn, MissingCourse, RowRepair } from './repairs';
import { GradeSyncLink, useGradeSyncPath } from './gradeSyncPath';

const Note = ({ children }: { children: React.ReactNode }) => (
  <span className="tw-text-sm tw-text-spa-tertiary">{children}</span>
);

const courseName = (id: string | null, title: string | null) => title ?? `course ${id ?? '—'}`;

const AttachButton = ({ column, stafferId }: { column: AttachColumn; stafferId: string }) => (
  <TakeColumnButton
    confirm={column.kind === 'orphaned'
      ? `This column was made for assignment ${column.named_id ?? '—'}, which no longer exists. It is matched `
        + `by its title, "${column.label ?? ''}" — confirm that is this assignment.`
      : undefined}
    label={`Attach ${courseName(column.course_id, column.course_title)}`}
    lineItemId={column.line_item_id}
    ownerId={column.owner.id}
    stafferId={stafferId}
    title={`"${column.label ?? ''}" has a gradebook column there that nothing posts to`}
  />
);

const missingNote = (missing: MissingCourse[]) => (
  `Teacher: copy it into ${missing.map(course => courseName(course.course_id, course.title)).join(', ')}`
);

/** The fix for one assignment, in its own row — or why there is nothing to do. */
const RepairCell = ({ id, repair, stafferId }: { id: number; repair?: RowRepair; stafferId: string }) => {
  const navigate = useNavigate();
  const gradeSyncPath = useGradeSyncPath();

  if (repair == null) return null;

  const base = gradeSyncPath(`/assignments/${id}`);
  const from = repair.link?.created_from;

  return (
    <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
      {(repair.teacher.length > 0 || repair.missing.length > 0) && (
        <>
          <SPAButton size="xs" type="button" variant="secondary" onClick={() => navigate(base)}>
            Message the teacher
          </SPAButton>
          {repair.teacher.map(code => <Note key={code}>{TEACHER_FIXES[code]}</Note>)}
          {repair.missing.length > 0 && <Note>{missingNote(repair.missing)}</Note>}
        </>
      )}
      {repair.columns.map(column => (
        <TakeColumnButton
          key={column.line_item_id}
          label={`Move the gradebook link here · course ${column.course_id ?? '—'}`}
          lineItemId={column.line_item_id}
          ownerId={column.owner.id}
          stafferId={stafferId}
          title={`"${column.label ?? ''}" — assignment ${column.held_by.map(holder => holder.id).join(', ')} `
            + 'is posting to it now'}
        />
      ))}
      {repair.attach.map(column => <AttachButton key={column.line_item_id} column={column} stafferId={stafferId} />)}
      {repair.link != null && (
        <>
          <SPAButton size="xs" type="button" variant="secondary" onClick={() => navigate(`${base}/columns`)}>
            Link to its column
          </SPAButton>
          {from != null && <Note>{`created from ${from.course_title ?? from.course_id}`}</Note>}
        </>
      )}
      {repair.unassign > 0 && (
        <SPAButton size="xs" type="button" variant="secondary" onClick={() => navigate(`${base}/assignees`)}>
          {`Unassign ${repair.unassign} ${repair.unassign === 1 ? 'class' : 'classes'}`}
        </SPAButton>
      )}
      {repair.trapped.map(column => (
        <Note key={column.line_item_id}>
          {`The LMS gradebook in ${courseName(column.course_id, column.course_title)} names this, `
            + 'but another copy is already sending grades there — leave it'}
        </Note>
      ))}
      {!needsAction(repair) && repair.holding.map(column => (
        <Note key={column.line_item_id}>
          {`Sending grades to a gradebook column that belongs to ${column.owner.id} `
            + `(${courseName(column.course_id, column.course_title)})`}
        </Note>
      ))}
      {repair.duplicate && (
        <Note>
          {repair.servedBy.length === 0 ? 'Duplicate — other copies serve its classes' : (
            <>
              {'Duplicate — its classes\' grades go through '}
              {repair.servedBy.map((copyId, index) => (
                <React.Fragment key={copyId}>
                  {index > 0 && ', '}
                  <GradeSyncLink
                    to={`/assignments/${copyId}`}
                  >
                    {copyId}
                  </GradeSyncLink>
                </React.Fragment>
              ))}
            </>
          )}
        </Note>
      )}
    </div>
  );
};

export default RepairCell;
